/**
 * Price Comparison Model - Encapsulates price comparison data and provides clean interface for UI components
 * This model handles data transformation and formatted properties
 */
class PriceComparisonModel {
  private _serverData: any;

  constructor(serverData: any = null) {
    this._serverData = serverData;
  }

  // ===== SERVER DATA PROPERTIES =====

  get store_id() {
    return this._serverData?.store_id ?? '';
  }

  get store_name() {
    return this._serverData?.store_name ?? '';
  }

  get item_name() {
    return this._serverData?.item_name ?? '';
  }

  get department_name() {
    return this._serverData?.department_name ?? '';
  }

  get tax_type() {
    return this._serverData?.tax_type ?? '';
  }

  get item_price() {
    return this._serverData?.item_price ?? 0;
  }

  get is_sold() {
    return this._serverData?.is_sold ?? false;
  }

  get is_available() {
    return this._serverData?.is_available ?? false;
  }

  get status() {
    return this._serverData?.status ?? null;
  }

  // ===== FORMATTED PROPERTIES =====

  get formattedStoreName() {
    return this.store_name || '-';
  }

  get formattedItemName() {
    return this.item_name || '-';
  }

  get formattedDepartment() {
    return this.department_name || '-';
  }

  get formattedTaxType() {
    return this.tax_type || '-';
  }

  get formattedPrice() {
    return this._formatCurrency(this.item_price);
  }

  // ===== UI FORMAT (for table rows) =====

  toRowFormat() {
    return {
      storeId: this.store_id,
      storeName: this.store_name,
      itemName: this.item_name,
      department: this.department_name,
      taxType: this.tax_type,
      price: this.item_price,
      is_sold: this.is_sold,
      is_available: this.is_available,
      status: this.status,
    };
  }

  // ===== HELPER METHODS =====

  _formatCurrency(value: number | null | undefined): string {
    if (value === null || value === undefined) {
      return '0.00';
    }
    return Number(value).toFixed(2);
  }

  // ===== UTILITY METHODS =====

  getRawData() {
    return this._serverData;
  }

  // ===== STATIC METHODS =====

  static fromServerData(serverData: any) {
    return new PriceComparisonModel(serverData);
  }

  static createEmpty() {
    return new PriceComparisonModel();
  }
}

export default PriceComparisonModel;
